import type { ErpModule } from '@/api/platform.api';
import { SUBSCRIPTION_VALIDATION_ENABLED } from '@/config/subscriptionPolicy';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { resolveRouteFeature } from '@/utils/navigation';
import { isRouteAllowedForEntryModule, getPostLoginPath, isHrEssOnlyUser, HR_ESS_HOME_PATH } from '@/utils/moduleScope';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { isStaleModulePath } from '@/utils/moduleNavigation';
import { isGuardedModulePath, isLockedModulePath } from '@/utils/routeGuardUtils';
import { resolveOrgRoutePermission } from '@/utils/navigation';
import { isOpenAccessRoute } from '@/utils/openAccessRoutes';
import { i18n } from '@/i18n';

function accessMessage(key: string, fallback: string, options?: Record<string, unknown>): string {
  if (i18n.isInitialized) {
    return i18n.t(`access.${key}`, { ns: 'common', defaultValue: fallback, ...options });
  }
  return fallback;
}

export type RouteAccessDenial = {
  message: string;
  fallbackPath: string;
  /** Redirect only — skip error toast (e.g. mid workspace switch). */
  silent?: boolean;
};

export function featureDeniedMessage(featureCode: string): string {
  return accessMessage(
    'featureNotEnabled',
    `Feature not enabled: ${featureCode}. Upgrade your subscription to unlock.`,
    { featureCode },
  );
}

export function evaluateOrgRouteDenial(params: {
  pathname: string;
  permissions: readonly string[];
  roleCode?: string | null;
  masterAdmin: boolean;
  entryModule: ErpModule | null | undefined;
  scopedModule: ErpModule | null;
  routePermission?: string | string[] | null;
  /** Backend-driven canView from session.screenActions or GET /v1/ui/screens/actions */
  backendCanView?: boolean;
  hasFeature: (code: string) => boolean;
  featuresLoading: boolean;
  accessibleModules?: string[];
}): RouteAccessDenial | null {
  const {
    pathname,
    permissions,
    roleCode,
    masterAdmin,
    entryModule,
    scopedModule,
    routePermission,
    backendCanView,
    hasFeature,
    featuresLoading,
    accessibleModules,
  } = params;

  if (masterAdmin) return null;
  if (isLockedModulePath(pathname)) return null;

  const revertFallback = () =>
    getPostLoginPath(
      scopedModule ?? entryModule ?? null,
      scopedModule ? '/pos/dashboard' : MODULE_HUB_PATH,
      { permissions, accessibleModules, roleCode },
    );

  const effectivePermission =
    routePermission !== undefined ? routePermission : resolveOrgRoutePermission(pathname);

  const allowedByRoutePermission =
    permissions.includes('MASTER_ADMIN') ||
    permissions.includes('ORG_MANAGE') ||
    (effectivePermission
      ? Array.isArray(effectivePermission)
        ? effectivePermission.some((p) => permissions.includes(p))
        : permissions.includes(effectivePermission)
      : false);

  if (backendCanView === false) {
    if (isOpenAccessRoute(pathname)) {
      const featureCode = resolveRouteFeature(pathname);
      if (!featureCode || featuresLoading || hasFeature(featureCode)) {
        // Open routes stay reachable for all workspace users when the feature (if any) is enabled.
      } else {
        return {
          message: featureDeniedMessage(featureCode),
          fallbackPath: revertFallback(),
        };
      }
    } else {
      const fallback =
        isHrEssOnlyUser(permissions, accessibleModules, roleCode) &&
        (pathname === '/hr' || pathname.startsWith('/hr/'))
          ? HR_ESS_HOME_PATH
          : revertFallback();
      return {
        message: accessMessage('pagePermissionDenied', 'You do not have permission to view this page.'),
        fallbackPath: fallback,
      };
    }
  }

  if (backendCanView !== true) {
    if (effectivePermission === undefined && isGuardedModulePath(pathname)) {
      const fallback =
        isHrEssOnlyUser(permissions, accessibleModules, roleCode) &&
        (pathname === '/hr' || pathname.startsWith('/hr/'))
          ? HR_ESS_HOME_PATH
          : revertFallback();
      return {
        message: accessMessage('pageNotAvailableForRole', 'This page is not available for your role.'),
        fallbackPath: fallback,
      };
    }

    if (effectivePermission && !allowedByRoutePermission) {
      const fallback =
        isHrEssOnlyUser(permissions, accessibleModules, roleCode) &&
        (pathname === '/hr' || pathname.startsWith('/hr/'))
          ? HR_ESS_HOME_PATH
          : revertFallback();
      return {
        message: accessMessage('pagePermissionDenied', 'You do not have permission to view this page.'),
        fallbackPath: fallback,
      };
    }
  }

  if (
    scopedModule &&
    pathname !== '/onboarding' &&
    !isPlatformAdminRoute(pathname) &&
    !isRouteAllowedForEntryModule(pathname, scopedModule)
  ) {
    if (isStaleModulePath(pathname, scopedModule)) {
      return {
        message: accessMessage('pageNotAvailableInModule', 'This page is not available in your current module.'),
        fallbackPath: revertFallback(),
        silent: true,
      };
    }
    return {
      message: accessMessage('pageNotAvailableInModule', 'This page is not available in your current module.'),
      fallbackPath: revertFallback(),
    };
  }

  const scopedToEntryModule =
    isPlatformAdminRoute(pathname) ||
    (scopedModule && isRouteAllowedForEntryModule(pathname, scopedModule));

  if (!scopedToEntryModule) {
    const featureCode = resolveRouteFeature(pathname);
    if (
      SUBSCRIPTION_VALIDATION_ENABLED &&
      featureCode &&
      !featuresLoading &&
      !hasFeature(featureCode)
    ) {
      return {
        message: featureDeniedMessage(featureCode),
        fallbackPath: revertFallback(),
      };
    }
  }

  return null;
}

export function evaluatePosRouteDenial(params: {
  pathname: string;
  permissions: readonly string[];
  masterAdmin: boolean;
  routePermission?: string | string[] | null;
  hasFeature: (code: string) => boolean;
  featuresLoading: boolean;
  posRouteFeatures: Record<string, string | string[] | null>;
  entryModule: ErpModule | null | undefined;
}): RouteAccessDenial | null {
  const {
    pathname,
    permissions,
    masterAdmin,
    routePermission,
    hasFeature,
    featuresLoading,
    posRouteFeatures,
    entryModule,
  } = params;

  const revertFallback = () =>
    entryModule ? getPostLoginPath(entryModule, MODULE_HUB_PATH) : MODULE_HUB_PATH;

  if (!masterAdmin && routePermission) {
    const allowed =
      permissions.includes('MASTER_ADMIN') ||
      (Array.isArray(routePermission)
        ? routePermission.some((p) => permissions.includes(p))
        : permissions.includes(routePermission));
    if (!allowed) {
      return {
        message: accessMessage('posTerminalPermissionDenied', 'You do not have permission to use the POS terminal.'),
        fallbackPath: revertFallback(),
      };
    }
  }

  const posFeature = posRouteFeatures[pathname];
  const featureCode = typeof posFeature === 'string' ? posFeature : null;
  if (
    SUBSCRIPTION_VALIDATION_ENABLED &&
    featureCode &&
    !featuresLoading &&
    !hasFeature(featureCode)
  ) {
    return {
      message: featureDeniedMessage(featureCode),
      fallbackPath: revertFallback(),
    };
  }
  return null;
}

export function evaluateModuleScopeDenial(params: {
  pathname: string;
  scopedModule: ErpModule | null;
}): RouteAccessDenial | null {
  const { pathname, scopedModule } = params;
  if (!scopedModule) return null;
  if (isRouteAllowedForEntryModule(pathname, scopedModule)) return null;
  if (isStaleModulePath(pathname, scopedModule)) {
    const home = getPostLoginPath(scopedModule, MODULE_HUB_PATH);
    return {
      message: accessMessage('pageNotAvailableInModule', 'This page is not available in your current module.'),
      fallbackPath: home,
      silent: true,
    };
  }
  const home = getPostLoginPath(scopedModule, MODULE_HUB_PATH);
  return {
    message: 'This page is not available in your current module.',
    fallbackPath: home,
  };
}
